import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { SupplierForm, type SupplierFormData } from '@client/components/suppliers/SupplierForm';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Form, Space } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/vendor-management/suppliers/add')({
    staticData: {
        metadata: {
            title: 'New supplier',
        },
        ui: {
            widthVariant: 'compact',
            // Wider than the compact default so the sectioned layout's
            // side-label column and paired fields get breathing room
            // (mirrors the new-incident form).
            className: 'w-6xl max-w-full',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <SupplierAdd />
        </PageController>
    );
}

function SupplierAdd() {
    const orgId = useOrgId();
    const formId = useId();
    const { mutate, isPending } = trpc.supplier.createSupplier.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<SupplierFormData>();

    return (
        <Card>
            <SupplierForm
                id={formId}
                form={form}
                sectioned
                onFinish={(values) => {
                    message.loading('Saving...');

                    mutate(values, {
                        onSuccess({ id }) {
                            navigate({
                                to: '/console/$orgId/configuration/vendor-management/suppliers/list/details/$id',
                                params: { orgId, id },
                            });

                            message.success('Saved.');
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    });
                }}
            />
            <Space className="flex justify-end border-separator border-t pt-4">
                <Button.Link
                    variant="secondary"
                    to="/console/$orgId/configuration/vendor-management/suppliers/list"
                    params={{ orgId }}
                >
                    Cancel
                </Button.Link>
                <Button type="primary" htmlType="submit" form={formId} loading={isPending}>
                    Save
                </Button>
            </Space>
        </Card>
    );
}
